import { lazy, Suspense, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { obtenerContrato } from './api';
import type { Contrato } from './api';
import { mensajeDeContrato } from './mensajes';

/**
 * El contrato de la API, navegable (KAN-184).
 *
 * Para quien integra un sistema con el facturador —sobre todo un equipo que no
 * lo escribió—: qué se puede pedir, qué contesta, y qué pasa cuando algo falla,
 * que es la parte cara de esta API. Todo sale de `docs/openapi.yaml`, que es el
 * archivo con el que el backend se compila y del que se generan los tipos de
 * este front.
 *
 * # POR QUÉ ESTÁ DENTRO DE LA APP Y NO PUBLICADO
 *
 * Es una decisión de Alex (KAN-184): el contrato no tiene secretos, pero
 * describe la superficie completa —las rutas de `/admin` incluidas— y no hay
 * razón para que la vea cualquiera. Servido por nginx quedaría **fuera** de la
 * cadena deny-by-default de Spring, a diferencia de toda la API. Así, el que no
 * tiene un usuario no lo lee, y el acceso se maneja donde ya se manejan los
 * demás: en el ABM de usuarios.
 *
 * Cualquier persona con sesión lo ve, sin `RequiereRol`: un `OPERADOR` alcanza.
 * No hace falta ser administrador para leer cómo funciona la API.
 */

// Diferido a propósito: es más de un mega de JavaScript que ninguna otra
// pantalla necesita. Ver `VisorDelContrato` por qué es Swagger UI.
const VisorDelContrato = lazy(() => import('./VisorDelContrato'));

export function PantallaDelContrato() {
  const contrato = useQuery({
    queryKey: ['contrato'],
    queryFn: obtenerContrato,
    // El contrato cambia con un despliegue, no entre pantallas. Cinco minutos
    // evitan volver a bajar ~180 KB cada vez que se vuelve acá, sin dejar a
    // nadie mirando una versión vieja mucho rato.
    staleTime: 5 * 60_000,
  });

  return (
    <main className="contrato">
      <h1>Contrato de la API</h1>
      <p>
        Este es el contrato del servicio que está corriendo ahora. Es <strong>solo para leer</strong>:
        desde acá no se puede ejecutar ningún pedido, porque el servicio emite contra ARCA{' '}
        <strong>producción</strong> y cada comprobante emitido es fiscal y no se puede borrar.
      </p>

      {contrato.isPending ? <p role="status">Trayendo el contrato…</p> : null}

      {contrato.isError ? (
        <p role="alert" className="error">
          {mensajeDeContrato(contrato.error)}
        </p>
      ) : null}

      {contrato.data ? <ContratoCargado contrato={contrato.data} /> : null}
    </main>
  );
}

function ContratoCargado({ contrato }: { contrato: Contrato }) {
  return (
    <>
      <p>
        {contrato.version ? (
          <>
            Versión <strong>{contrato.version}</strong>.{' '}
          </>
        ) : null}
        Para generar un cliente en tu lenguaje, bajá el archivo:{' '}
        <BotonDeDescarga contrato={contrato} />. Un sistema conectado lo puede pedir directo con su
        API key en <code>GET /api/v1/contrato</code>.
      </p>
      {/* Fondo claro forzado: los estilos de Swagger UI asumen texto oscuro sobre
          blanco y son ilegibles sobre el fondo oscuro de `color-scheme: dark`. */}
      <div className="contrato-visor">
        <Suspense fallback={<p role="status">Cargando el visor…</p>}>
          <VisorDelContrato documento={contrato.documento} />
        </Suspense>
      </div>
    </>
  );
}

/**
 * Baja el YAML **tal como llegó**, no una reserialización del documento: lo que
 * se descarga tiene que ser byte por byte lo que sirve el backend, que es lo que
 * se revisa en el diff de un merge request.
 *
 * Es un botón y no un `<a href>` por lo mismo que en el PDF: el archivo ya está
 * en memoria porque se pidió con la sesión, y un enlace al endpoint no llevaría
 * el `Authorization`.
 */
function BotonDeDescarga({ contrato }: { contrato: Contrato }) {
  const urls = useRef<string[]>([]);

  useEffect(() => {
    const creadas = urls.current;
    return () => {
      creadas.forEach((url) => URL.revokeObjectURL(url));
      creadas.length = 0;
    };
  }, []);

  function descargar(): void {
    const url = URL.createObjectURL(new Blob([contrato.texto], { type: 'application/yaml' }));
    urls.current.push(url);
    const enlace = document.createElement('a');
    enlace.href = url;
    // Con la versión en el nombre: dos descargas de días distintos no se pisan
    // ni se confunden en la carpeta de descargas.
    enlace.download = contrato.version ? `openapi-${contrato.version}.yaml` : 'openapi.yaml';
    enlace.click();
  }

  return (
    <button type="button" onClick={descargar}>
      Descargar openapi.yaml
    </button>
  );
}
